// src/utils/assistenteGuida.js
// ═══════════════════════════════════════════════════════════════════════════
// ASSISTENTE DELLA GUIDA — «Come posso aiutarti?» (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Gratuito e senza intelligenza artificiale: cerca la domanda nel testo delle schede del
// Centro assistenza (titolo, parole chiave, sintesi, «dove si trova», passi, campi e
// problemi frequenti) e propone le schede migliori con il passaggio più vicino alla domanda.
// Gira tutto nel browser: nessun servizio esterno, nessun costo, nessun dato che esce.
// Se non trova una risposta sicura lo dice e propone di scrivere all'assistenza.
//
// Stesso file per app e sito: demo/guida/genera.py lo copia in vetrina/guida/assistente.js
// (per questo non importa nulla). I dati delle schede li genera sempre genera.py:
// src/utils/indiceGuidaDati.js (app) e vetrina/guida/indice-ricerca.js (sito).
// ═══════════════════════════════════════════════════════════════════════════

// Parole che non aiutano a capire cosa si cerca
const VUOTE = new Set(("il lo la i gli le un uno una di a da in con su per tra fra e o ma se che chi cui non ne ci vi si mi ti " +
  "del dello della dei degli delle al allo alla ai agli alle dal dallo dalla dai dagli dalle nel nello nella nei negli nelle " +
  "sul sullo sulla sui sugli sulle col coi è e' sono sei siamo ho hai ha abbiamo hanno essere avere fare faccio fa fanno " +
  "come dove quando quale quali cosa cos perché perche posso puoi può puo possiamo devo deve devono vorrei voglio serve " +
  "mio mia miei mie tuo tua suo sua nostro nostra questo questa questi queste quello quella quelli quelle " +
  "c è c'è ce anche ancora già gia più piu molto poi solo tutto tutti tutte ogni nexiora app modo bisogna riesco riuscire quanto quanti").split(/\s+/));

// «vedo», «trovo»: non dicono di cosa si parla (non contano per scegliere la scheda), ma
// aiutano a scegliere il passaggio giusto («Un collega non vede la commessa»)
const VERBI = new Set(["vedo", "vedere", "vede", "vedono", "trovo", "trovare", "trova", "trovano"]);

// Parole di tutti i giorni → parole usate nella guida (solo equivalenze sicure)
const SINONIMI = {
  progetto: "commessa", progetti: "commesse", cantiere: "commessa", lavoro: "commessa",
  task: "attività", compito: "attività", compiti: "attività",
  fattura: "fatturazione", fatture: "fatturazione", fatturare: "fatturazione",
  operaio: "dipendente", operai: "dipendenti", tecnico: "dipendente", tecnici: "dipendenti", personale: "dipendenti",
  ore: "ore", cartellino: "presenze", timbratura: "presenze", timbrare: "presenze",
  preventivo: "offerta", preventivi: "offerte",
  permessi: "abilitazioni", autorizzazioni: "abilitazioni",
  collega: "utente", colleghi: "utenti", invitare: "invita",
  rapporto: "rapportino", rapportini: "rapportino", intervento: "rapportino",
  noleggio: "noleggi", gru: "noleggi", piattaforma: "noleggi",
  scadenza: "scadenzario", scadenze: "scadenzario", scadenziario: "scadenzario",
  materiale: "materiali", ordine: "ordini", fornitore: "fornitori",
  notifica: "notifiche", avviso: "notifiche", avvisi: "notifiche", popup: "notifiche",
  cellulare: "telefono", smartphone: "telefono",
  guadagno: "margine",
  appunto: "note", appunti: "note", promemoria: "note",
};

// Domande che non riguardano una scheda della guida: risposte fisse, verificate sull'app
const FISSE = [
  { parole: ["password"], titolo: "Password dimenticata",
    x: "Nella pagina di accesso scrivi la tua email e premi «Password dimenticata?»: se l'indirizzo è registrato ricevi un'email con il link per sceglierne una nuova. Controlla anche la cartella spam.",
    link: "https://app.nexiora.it" },
  // W428: prima della formazione, perché «demo in video call» deve trovare questa
  { parole: ["demo", "dimostrazione", "dimostrativa"], insieme: [["vedere", "prima"], ["mostrarmi", "nexiora"], ["farmi", "vedere"], ["vederla", "funzionare"]],
    titolo: "Demo gratuita in video call",
    x: "Se non sei ancora cliente puoi prenotare una demo gratuita di 20 minuti in video call: condividiamo lo schermo e tu guardi dal tuo computer, senza installare niente. Scegli giorno e orario sul sito; la confermiamo noi per email. Oppure provi Nexiora gratis per 3 mesi, senza carta.",
    link: "https://nexiora.it/demo-video-call.html" },
  // prima di «prezzi»: «quanto costa la formazione» deve trovare questa (numeri = FORMAZIONE in utils/pianiNexiora.js)
  { parole: ["formazione", "corso", "corsi", "videocall", "affiancamento", "addestramento", "training", "lezione", "lezioni"],
    insieme: [["video", "call"], ["impostare", "insieme"], ["chi", "insegna"]],
    titolo: "Formazione su Nexiora",
    x: "Non è obbligatoria. Durante la prova c'è un avvio gratuito di 45 minuti in video call, da chiedere entro i primi 30 giorni. Poi: 3 ore in video call €450, 3 ore in presenza €600 più trasferta, ora singola €200; ore aggiuntive a un pacchetto €90. IVA esclusa, su prenotazione e secondo disponibilità. Titolare e amministratori scelgono giorno e orario tra quelli liberi da La mia area → 🎓 Richiedi formazione; se nessuna data va bene, lasciano una richiesta. L'avvio gratuito è uno per azienda.",
    link: "https://nexiora.it/formazione.html" },
  { parole: ["prezzo", "prezzi", "costa", "listino", "abbonamento", "pagamento", "pagare"], titolo: "Prezzi e piani",
    // numeri = LISTINO (Rev3) in utils/pianiNexiora.js: la prova prove/prova_nexi_novita.mjs controlla che coincidano
    x: "Il Pro costa €49 al mese fino a 5 utenti, il Gold €65. Oltre, i posti si aggiungono a blocchi di 5: €6 a posto dal 6° al 10° (Gold €8) e €5 dall'11° in poi (Gold €7). Prezzi IVA esclusa; pagando l'anno in anticipo c'è lo sconto del 10%. Tutti i dettagli nella pagina Prezzi.", link: "https://nexiora.it/prezzi.html" },
  { parole: ["supporto", "helpdesk"], insieme: [["chiedo", "assistenza"], ["chiedere", "assistenza"], ["contattare", "assistenza"], ["assistenza", "telefono"], ["assistenza", "nexiora"], ["parlare", "qualcuno"]],
    titolo: "Assistenza",
    x: "In tutti i piani hai la guida, Nexi e l'assistenza via email a support@nexiora.it nei giorni lavorativi. Con il piano Gold, o da 10 posti in su, anche al telefono o in video call, su appuntamento.",
    link: "https://nexiora.it/condizioni.html" },
  { parole: ["gratis", "gratuita", "gratuito", "gratuitamente"], titolo: "Prova gratuita",
    x: "Ti registri e usi Nexiora per 3 mesi con tutti i moduli, anche quelli Gold, senza carta di credito e senza addebiti automatici. Alla fine scegli il piano Pro o Gold; se non lo attivi, Nexiora resta consultabile in sola lettura e i tuoi dati non vengono cancellati.",
    link: "https://nexiora.it/prezzi.html" },
  { parole: ["installare", "installo", "installazione", "scaricare", "scarico", "android", "iphone", "ios", "store"],
    insieme: [["funziona", "telefono"], ["funziona", "cellulare"], ["funziona", "tablet"], ["funziona", "smartphone"], ["usare", "telefono"], ["usarla", "telefono"]],
    titolo: "Telefono, tablet e computer",
    x: "Nexiora funziona dal browser di computer, tablet e telefono, senza installare programmi. Dal telefono puoi aggiungerla alla schermata Home e usarla come un'app, con le notifiche.",
    link: "https://nexiora.it/domande-frequenti.html" },
];

// Che tipo di domanda è: un problema («non…», «errore», «vuoto») o un «come si fa»
const PAROLE_PROBLEMA = ["non", "errore", "errori", "manca", "mancano", "vuoto", "vuota", "sbagliato", "sbagliata", "bloccato", "bloccata", "funziona", "arriva", "arrivano", "vedo", "compare", "riesco", "problema"];
function intento(domanda) {
  const w = normalizza(domanda).split(" ");
  if (w.some(x => PAROLE_PROBLEMA.includes(x))) return "problema";
  if (w.some(x => ["come", "dove", "quando", "creo", "creare", "aggiungo", "aggiungere", "inserisco", "inserire"].includes(x))) return "come";
  return "";
}
const PESO_BLOCCO = {
  problema: { problema: 2, passo: 1, campo: 0.8, serve: 0.6, quando: 0.6 },
  come: { passo: 1.6, problema: 0.35, campo: 0.8, serve: 0.6, quando: 0.8 },
  "": { passo: 1.2, problema: 1, campo: 1, serve: 0.8, quando: 0.8 },
};

/** Risposta fissa (password, prezzi) se la domanda riguarda quello, altrimenti null */
export function rispostaFissa(domanda) {
  const w = normalizza(domanda).split(" ");
  return FISSE.find(f => f.parole.some(p => w.includes(p)) || (f.insieme || []).some(g => g.every(p => w.includes(p)))) || null;
}

/** minuscole, senza accenti né punteggiatura */
export function normalizza(t) {
  return String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/<[^>]+>/g, " ").replace(/[^a-z0-9]+/g, " ").trim();
}

// Radice semplice: toglie la vocale finale e qualche desinenza frequente (rapportino/rapportini,
// commessa/commesse, fatturazione/fatturazioni, firmare/firmato/firma)
export function radice(p) {
  let r = p;
  if (r.length > 6) r = r.replace(/(azione|azioni|amento|amenti|mente)$/, "");
  if (r.length > 5) r = r.replace(/(are|ere|ire|ato|ata|ati|ate|ito|ita|iti|ite)$/, "");
  if (r.length > 4) r = r.replace(/[aeio]$/, "");
  return r;
}

// conVerbi: nei testi della guida i verbi restano (servono per riconoscere il passaggio)
function parole(testo, conVerbi = false) {
  return normalizza(testo).split(" ").filter(w => w && w.length > 1 && !VUOTE.has(w) && (conVerbi || !VERBI.has(w)));
}

/** Parole della domanda (con i sinonimi), già ridotte a radice e senza doppioni */
export function paroleDomanda(domanda) {
  const out = [];
  parole(domanda).forEach(w => {
    [w, ...(SINONIMI[w] ? parole(SINONIMI[w]) : [])].forEach(x => { const r = radice(x); if (r.length > 1 && !out.includes(r)) out.push(r); });
  });
  return out;
}

const insieme = testo => new Set(parole(testo, true).map(radice));
// una parola della domanda «c'è» se coincide con una del testo, o ne è l'inizio (≥ 4 lettere)
function presente(r, set) {
  if (set.has(r)) return true;
  if (r.length < 4) return false;
  for (const w of set) if (w.length >= 4 && (w.startsWith(r) || r.startsWith(w))) return true;
  return false;
}

// Indice pronto per la ricerca (calcolato una volta sola per elenco di schede)
const preparati = new WeakMap();
function prepara(indice) {
  if (preparati.has(indice)) return preparati.get(indice);
  const p = indice.map(s => ({
    s,
    titolo: insieme(s.t), chiavi: insieme(s.k), sintesi: insieme(s.s), dove: insieme(s.d || ""),
    blocchi: (s.b || []).map(b => ({ b, dom: insieme(b.q || ""), testo: insieme(b.x || "") })),
  }));
  preparati.set(indice, p);
  return p;
}

/**
 * Le schede che rispondono meglio alla domanda (al massimo `max`), ognuna con il passaggio
 * più vicino: [{ id, titolo, area, sintesi, punteggio, copertura, risposta: { tipo, q, x, ancora } }].
 * Vuoto se la domanda non ha parole utili o nessuna scheda è abbastanza vicina.
 */
export function cercaNellaGuida(indice, domanda, max = 3) {
  const q = paroleDomanda(domanda);
  if (!q.length || !Array.isArray(indice)) return [];
  const pesi = PESO_BLOCCO[intento(domanda)];
  const parole = normalizza(domanda).split(" ");
  const cercaPosto = parole[0] === "dove" && ["trovo", "trova", "trovare", "sono", "sta", "stanno", "e", "si"].includes(parole[1]);
  const verbi = [...new Set(normalizza(domanda).split(" ").filter(w => VERBI.has(w)).map(w => w.slice(0, w.startsWith("tr") ? 4 : 3)))];
  const risultati = prepara(indice).map(p => {
    let punti = 0, trovate = 0, forti = 0;
    q.forEach(r => {
      const peso = presente(r, p.titolo) ? 8 : presente(r, p.chiavi) ? 6 : presente(r, p.sintesi) ? 3 : presente(r, p.dove) ? 2
        : p.blocchi.some(x => presente(r, x.dom) || presente(r, x.testo)) ? 1 : 0;
      if (peso) { punti += peso; trovate++; }
      if (peso >= 3) forti++;
    });
    const copertura = trovate / q.length;
    // passaggio migliore: quello che contiene più parole della domanda (le domande dei
    // «Problemi frequenti» valgono di più, perché sono già scritte come domande)
    let migliore = null, puntiBlocco = 0;
    p.blocchi.forEach(x => {
      let v = 0;
      q.forEach(r => { if (presente(r, x.dom)) v += x.b.tipo === "problema" ? 3 : 1.5; else if (presente(r, x.testo)) v += 1; });
      if (v) verbi.forEach(r => { if ([...x.dom].some(w => w.startsWith(r))) v += x.b.tipo === "problema" ? 2 : 0.5; });
      v *= pesi[x.b.tipo] || 1;
      if (v > puntiBlocco) { puntiBlocco = v; migliore = x.b; }
    });
    // la domanda è solo il nome della scheda («le mie ore»): si risponde con la sua sintesi,
    // non con un passaggio qualsiasi che contiene le stesse parole
    const soloTitolo = q.length <= 3 && q.every(r => presente(r, p.titolo));
    // W444: «dove trovo / dove sono / dove si trova …» chiede la posizione nel menu: si risponde
    // con il «Dove si trova» della scheda (aggiornato con la barra laterale riorganizzata)
    const risposta = cercaPosto && p.s.d ? { tipo: "dove", x: p.s.d, ancora: "" } : soloTitolo ? null : migliore;
    return { id: p.s.id, titolo: p.s.t, area: p.s.a, sintesi: p.s.s, punteggio: punti * (0.5 + copertura) + puntiBlocco * 0.6, copertura, forti, risposta };
  // almeno metà delle parole trovate e almeno una nel titolo, nelle parole chiave o nella
  // sintesi: una parola trovata solo per caso in mezzo a un testo non basta
  }).filter(r => r.copertura >= (q.length === 1 ? 1 : 0.5) && r.forti > 0 && r.punteggio >= 8);
  risultati.sort((a, b) => b.punteggio - a.punteggio);
  if (!risultati.length) return [];
  // si tengono solo le schede non troppo lontane dalla prima
  const soglia = risultati[0].punteggio * 0.45;
  return risultati.filter(r => r.punteggio >= soglia).slice(0, max);
}

/** Testo del passaggio, senza tag e tagliato a fine parola */
export function testoRisposta(risposta, n = 260) {
  if (!risposta) return "";
  const t = String(risposta.x || "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  if (t.length <= n) return t;
  return t.slice(0, n - 1).replace(/\s+\S*$/, "") + "…";
}
